/**
 * money.js — render integer minor-units (paise/cents) for display.
 *
 * PAYMENT-MONEY-PRECISION-1 Stage 2. `amountMinor` (integer paise) is the
 * authoritative money value on the wire; this module is the FRONTEND rendering
 * edge, a 1:1 port of backend `utils/money.py::format_money` so the two
 * runtimes produce byte-identical strings for the same paise value. That
 * cross-runtime parity is asserted by `__tests__/money.test.js` (JS side) and
 * `backend/tests/test_iter200_money_minor_units.py` (Python side) against the
 * SAME (paise → string) table — keep both in sync; a divergence is a defect.
 *
 * Why a SECOND money formatter alongside locale.js `formatCurrency`?
 * `formatCurrency` takes a major-unit NUMBER and uses Intl (which drops a
 * trailing-zero paise digit — "₹1,234.5" not "₹1,234.50"). This module takes
 * integer paise and matches the backend's exact 2-decimal padding. Inputs
 * (user-typed rupees) still go through the existing rupee paths; this is the
 * DISPLAY edge for the paise wire authority only.
 *
 * Absent vs invalid: null/undefined/'' → "—" (absent, e.g. a non-payment row).
 * A non-numeric value throws (loud) — mirrors locale.js `toFinite`, and
 * deliberately does NOT silently fall back to a legacy float field.
 */

const ABSENT_PLACEHOLDER = '\u2014'; // em dash

// Minor-unit scale per ISO-4217 — mirror of backend `_MINOR_SCALE`.
const MINOR_SCALE = {
  BHD: 1000, KWD: 1000, OMR: 1000, TND: 1000, JOD: 1000, IQD: 1000,
  JPY: 1, KRW: 1, VND: 1, CLP: 1, ISK: 1,
};

// Symbols — mirror of backend `_SYMBOL` (NOT locale.js CURRENCY_SYMBOLS, which
// differs on AED). Unknown currency → "<CODE> " prefix, exactly like backend.
const SYMBOL = {
  INR: '\u20B9', USD: '$', EUR: '\u20AC', GBP: '\u00A3',
  JPY: '\u00A5', AUD: '$', CAD: '$', SGD: '$',
};

export function minorScale(currency = 'INR') {
  return MINOR_SCALE[(currency || 'INR').toUpperCase()] ?? 100;
}

/** Integer minor units → major-unit NUMBER (e.g. paise → rupees). */
export function minorToMajor(amountMinor, currency = 'INR') {
  return Number(amountMinor) / minorScale(currency);
}

/** Indian digit grouping: "380000" → "3,80,000", "10000000" → "1,00,00,000". */
function indianGroup(intStr) {
  if (intStr.length <= 3) return intStr;
  const last3 = intStr.slice(-3);
  let rest = intStr.slice(0, -3);
  const groups = [];
  while (rest.length > 2) {
    groups.unshift(rest.slice(-2));
    rest = rest.slice(0, -2);
  }
  if (rest) groups.unshift(rest);
  return groups.join(',') + ',' + last3;
}

function _body(amountMinor, cur) {
  const scale = minorScale(cur);
  let minor = Math.trunc(Number(amountMinor));
  const sign = minor < 0 ? '-' : '';
  minor = Math.abs(minor);
  const whole = scale > 1 ? Math.floor(minor / scale) : minor;
  const fracMinor = scale > 1 ? minor % scale : 0;
  const intPart = cur === 'INR' ? indianGroup(String(whole)) : whole.toLocaleString('en-US');
  const decPlaces = scale === 1 ? 0 : scale === 1000 ? 3 : 2;
  const body = decPlaces && fracMinor
    ? `${intPart}.${String(fracMinor).padStart(decPlaces, '0')}`
    : intPart;
  return { sign, body };
}

function _assertNumeric(amountMinor, fnName) {
  const n = Number(amountMinor);
  if (!Number.isFinite(n)) {
    throw new Error(`${fnName}: expected integer minor-units, received "${amountMinor}"`);
  }
}

/** Render minor units WITH currency symbol. e.g. formatMoneyMinor(38000000) → "₹3,80,000". */
export function formatMoneyMinor(amountMinor, currency = 'INR') {
  if (amountMinor == null || amountMinor === '') return ABSENT_PLACEHOLDER;
  _assertNumeric(amountMinor, 'formatMoneyMinor');
  const cur = (currency || 'INR').toUpperCase();
  const { sign, body } = _body(amountMinor, cur);
  const symbol = SYMBOL[cur] ?? `${cur} `;
  return `${sign}${symbol}${body}`;
}

/** Render minor units as a grouped number WITHOUT symbol (symbol shown separately). */
export function formatNumberMinor(amountMinor, currency = 'INR') {
  if (amountMinor == null || amountMinor === '') return ABSENT_PLACEHOLDER;
  _assertNumeric(amountMinor, 'formatNumberMinor');
  const cur = (currency || 'INR').toUpperCase();
  const { sign, body } = _body(amountMinor, cur);
  return `${sign}${body}`;
}
